import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { useAuth } from "@/app/providers/useAuth";
import { useCommunitySafetyData } from "./useCommunitySafetyData";
import type {
  Step1ActiveTab,
  UseStep1ViewModelParams,
  UseStep1ViewModelResult,
} from "../model/propertiesTypes";

/**
 * Step 1 ViewModel
 * Owns Step 1 UI state, derived values, and navigation/auth side-effects.
 */
export function useStep1ViewModel({
  addressData,
  propertyDetails,
  onNext,
  onBack,
}: UseStep1ViewModelParams): UseStep1ViewModelResult {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [activeTab, setActiveTab] = useState<Step1ActiveTab>("crime");
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const { sexOffendersData, crimeIncidentsData, loading, error, mapMarkers } =
    useCommunitySafetyData({
      propertyDetails,
      addressData,
      activeTab,
      distance: 5000, // 5km radius (comment in Step1 previously said 1km but passed 5000)
    });

  const { addressString, city, state } = useMemo(() => {
    return {
      addressString: addressData?.addressString || "694 Center St, Chicopee, MA",
      city: addressData?.address?.city || "Chicopee",
      state: addressData?.address?.state || "MA",
    };
  }, [addressData?.addressString, addressData?.address?.city, addressData?.address?.state]);

  const assessedValue = useMemo(() => {
    return (
      propertyDetails?.taxInfo?.assessedValue ??
      propertyDetails?.taxInfo?.assessedImprovementValue ??
      0
    );
  }, [propertyDetails?.taxInfo?.assessedValue, propertyDetails?.taxInfo?.assessedImprovementValue]);

  const marketValue = useMemo(() => {
    return (
      propertyDetails?.taxInfo?.marketValue ?? propertyDetails?.taxInfo?.marketImprovementValue ?? 0
    );
  }, [propertyDetails?.taxInfo?.marketValue, propertyDetails?.taxInfo?.marketImprovementValue]);

  const calculatedPotentialSavings = useMemo(() => {
    const taxAmount = propertyDetails?.taxInfo?.taxAmount;
    console.log("taxAmount", taxAmount);
    return taxAmount ? Math.round(Number(taxAmount) * 0.08) : 0;
  }, [propertyDetails?.taxInfo?.taxAmount]);

  const handleBackClick = () => {
    // Explicit route: avoid navigate(-1) revisiting /login after auth restore (AuthLayoutWrapper → /).
    navigate(routes.propertiesSearch);
  };

  const protestFlowData = {
    addressData,
    propertyDetails,
    initialStep: 2,
    postAuthShowLanding: true,
  };

  const handleFileProtest = () => {
    if (!isAuthenticated) {
      sessionStorage.setItem("property_protest_flow", JSON.stringify(protestFlowData));

      navigate(routes.login, {
        state: {
          from: routes.properties,
          initialStep: 2,
          addressData,
          propertyDetails,
          postAuthShowLanding: true,
        },
      });
      return;
    }

    if (onNext) onNext();
    else console.log("File protest clicked");
  };

  const handleNextClick = () => {
    if (onNext) onNext();
  };

  const handlePrevClick = () => {
    if (onBack) onBack();
  };

  return {
    activeTab,
    setActiveTab,
    isMapModalOpen,
    setIsMapModalOpen,

    addressString,
    city,
    state,
    assessedValue,
    marketValue,
    calculatedPotentialSavings,

    sexOffendersData,
    crimeIncidentsData,
    loading,
    error,
    mapMarkers,

    handleBackClick,
    handleFileProtest,
    handleNextClick,
    handlePrevClick,
  };
}
